/**
 * Связанный док: docs/living/taxonomy/TEMPLATE_OKDOC_TAXONOMY_ARCHITECTURE.md
 * Если меняется mapping category / intent / manual eligibility, обновляйте и код, и док.
 */
import {
  getChannelLabel,
  getChannelDisplayName,
  normalizeTemplateMessenger,
  templateMatchesChannel,
} from '@/config/channels';

export type TemplateTechType = 'text' | 'waba_template';
export type TemplateCategoryId =
  | 'notification'
  | 'service'
  | 'marketing'
  | 'feedback'
  | 'birthday'
  | 'manual';
export type TemplateUsageFilter = '' | 'manual' | 'event_driven' | 'scheduled' | 'cluster' | 'post_procedure' | 'reply_chain';
export type TemplateScheduleType = '' | 'event_triggered' | 'daily_scheduled' | 'manual';

export interface TemplateTaxonomyLike {
  template_type?: string;
  external_template_id?: string;
  messenger?: string;
  category?: string;
  send_schedule?: {
    type?: string;
  } | null;
  buttons?: Array<unknown>;
}

export interface TemplateCategoryOption {
  value: TemplateCategoryId;
  label: string;
  shortLabel: string;
  description: string;
}

export const TEMPLATE_TECH_TYPE_OPTIONS = [
  { value: 'text', label: 'Внутренний' },
  { value: 'waba_template', label: 'WABA Template' },
] as const;

export const TEMPLATE_CATEGORIES: TemplateCategoryOption[] = [
  { value: 'notification', label: '📋 Уведомления', shortLabel: 'Уведомления', description: 'Напоминания, подтверждения записей' },
  { value: 'service', label: '🦷 По услугам', shortLabel: 'По услугам', description: 'Шаблоны после завершения приёма и по услугам' },
  { value: 'marketing', label: '📢 Маркетинг', shortLabel: 'Маркетинг', description: 'Рассылки, акции, приглашения' },
  { value: 'feedback', label: '⭐ Обратная связь', shortLabel: 'Обратная связь', description: 'Сбор отзывов и обратной связи' },
  { value: 'birthday', label: '🎂 День рождения', shortLabel: 'День рождения', description: 'Поздравления и birthday-сценарии' },
  { value: 'manual', label: '📨 Ручные', shortLabel: 'Ручные', description: 'Шаблоны для ручных рассылок по сегментам' },
];

export const TEMPLATE_THEME_OPTIONS = TEMPLATE_CATEGORIES.filter((category) => category.value !== 'manual');

export const SEND_SCHEDULE_TYPES = [
  { value: '', label: 'Без расписания' },
  { value: 'event_triggered', label: 'По событию' },
  { value: 'daily_scheduled', label: 'По расписанию' },
  { value: 'manual', label: 'Только вручную' },
] as const;

export const TEMPLATE_CHANNEL_FILTER_OPTIONS = [
  { value: 'whatsapp_regular', label: 'WhatsApp Regular' },
  { value: 'whatsapp_waba', label: 'WhatsApp WABA' },
  { value: 'telegram_bot', label: 'Telegram Bot' },
  { value: 'telegram_client', label: 'Telegram Client' },
  { value: 'max', label: 'MAX Messenger' },
  { value: 'max_client', label: 'MAX Client' },
  { value: 'sms', label: 'SMS' },
  { value: 'sms_tel', label: 'SMS-Tel' },
];

export const TEMPLATE_FORM_CHANNEL_OPTIONS = [
  { value: '', label: 'Любой канал (автовыбор)' },
  { value: 'whatsapp', label: 'WhatsApp Regular' },
  { value: 'telegram', label: 'Telegram Bot' },
  { value: 'telegram_bot', label: 'Telegram Bot' },
  { value: 'telegram_client', label: 'Telegram Client' },
  { value: 'sms', label: 'SMS' },
  { value: 'sms_tel', label: 'SMS-Tel' },
  { value: 'max', label: 'MAX Messenger' },
  { value: 'max_client', label: 'MAX Client' },
];

export const TEMPLATE_USAGE_FILTER_OPTIONS = [
  { value: '', label: 'Все сценарии' },
  { value: 'manual', label: 'Ручные' },
  { value: 'event_driven', label: 'Event-Driven' },
  { value: 'scheduled', label: 'По расписанию' },
  { value: 'cluster', label: 'Кластерные' },
  { value: 'post_procedure', label: 'После процедур' },
  { value: 'reply_chain', label: 'Auto-Reply' },
] as const;

export function isWabaBackedTemplate(template?: TemplateTaxonomyLike | null): boolean {
  return template?.template_type === 'waba_template' || !!template?.external_template_id;
}

export function getTechnicalTypeLabel(template?: TemplateTaxonomyLike | null): string {
  return isWabaBackedTemplate(template) ? 'WABA Template' : 'Внутренний';
}

export function getCategoryLabel(category?: string, decorated = true): string {
  const option = TEMPLATE_CATEGORIES.find((item) => item.value === category);
  if (!option) {
    return category || '';
  }
  return decorated ? option.label : option.shortLabel;
}

export function getScheduleTypeLabel(scheduleType?: string): string {
  switch (scheduleType) {
    case 'manual':
      return 'Только вручную';
    case 'event_triggered':
      return 'По событию';
    case 'daily_scheduled':
      return 'По расписанию';
    default:
      return '';
  }
}

export function matchesTemplateChannelFilter(template: TemplateTaxonomyLike, channelId: string): boolean {
  if (!channelId) {
    return true;
  }
  return templateMatchesChannel(template, channelId);
}

export function getTemplateChannelLabel(template?: TemplateTaxonomyLike | null): string {
  if (!template) {
    return '';
  }
  if (isWabaBackedTemplate(template)) {
    return getChannelLabel('whatsapp_waba');
  }

  const normalizedChannel = normalizeTemplateMessenger(template.messenger);
  if (!normalizedChannel) {
    return 'Любой канал';
  }

  return getChannelLabel(normalizedChannel);
}

export function getTemplateChannelName(template?: TemplateTaxonomyLike | null): string {
  if (!template) {
    return '';
  }
  if (isWabaBackedTemplate(template)) {
    return getChannelDisplayName('whatsapp_waba');
  }

  const normalizedChannel = normalizeTemplateMessenger(template.messenger);
  if (!normalizedChannel || normalizedChannel === 'universal') {
    return 'Любой канал';
  }

  return getChannelDisplayName(normalizedChannel);
}

/** Канонический channel id для иконки; null = «любой» / неизвестно. */
export function getTemplateChannelId(template?: TemplateTaxonomyLike | null): string | null {
  if (!template) {
    return null;
  }
  if (isWabaBackedTemplate(template)) {
    return 'whatsapp_waba';
  }
  const normalizedChannel = normalizeTemplateMessenger(template.messenger);
  if (!normalizedChannel || normalizedChannel === 'universal') {
    return null;
  }
  return normalizedChannel;
}

export function getTemplateCommunicationIntent(category?: string): string {
  switch (category) {
    case 'service':
      return 'service_followup';
    case 'marketing':
      return 'marketing';
    case 'feedback':
      return 'feedback';
    case 'birthday':
      return 'retention';
    case 'manual':
      return 'marketing';
    default:
      return 'notification';
  }
}

export function isManualBroadcastEligibleTemplate(template?: TemplateTaxonomyLike | null): boolean {
  if (!template) {
    return false;
  }
  const scheduleType = template.send_schedule?.type || '';
  const intent = getTemplateCommunicationIntent(template.category);
  return scheduleType === 'manual' || template.category === 'manual' || intent === 'marketing';
}
